<template>
  <div class="tags-view-container">
    <!-- Left scroll arrow -->
    <div
      v-if="showScrollBtns"
      class="scroll-btn scroll-btn-left"
      @click="scrollLeft"
    >
      <ChevronLeft :size="14" />
    </div>

    <!-- Tags scroll wrapper -->
    <div
      ref="scrollContainer"
      class="tags-view-wrapper"
      @wheel.prevent="handleWheel"
    >
      <router-link
        v-for="tag in tagsView"
        :key="tag.path"
        ref="tagRefs"
        :to="{ path: tag.path, fullPath: tag.fullPath, query: tag.query }"
        custom
        v-slot="{ navigate }"
      >
        <span
          class="tags-view-item"
          :class="{ active: isActive(tag) }"
          @click="navigate"
          @contextmenu.prevent="openMenu(tag, $event)"
        >
          {{ translateTitle(tag.title) }}
          <span
            v-if="!tag.affix"
            class="tag-close"
            @click.prevent.stop="closeTag(tag)"
          >
            <X :size="12" />
          </span>
        </span>
      </router-link>
    </div>

    <!-- Right scroll arrow -->
    <div
      v-if="showScrollBtns"
      class="scroll-btn scroll-btn-right"
      @click="scrollRight"
    >
      <ChevronRight :size="14" />
    </div>

    <!-- Context menu -->
    <ul
      v-show="contextMenu.visible"
      class="context-menu"
      :style="{ left: contextMenu.left + 'px', top: contextMenu.top + 'px' }"
    >
      <li @click="refreshPage">
        <RefreshCw :size="14" />
        <span>{{ t('tagsView.refresh') }}</span>
      </li>
      <li v-if="!isAffix(selectedTag)" @click="closeTag(selectedTag)">
        <X :size="14" />
        <span>{{ t('tagsView.close') }}</span>
      </li>
      <li @click="closeOtherTags">
        <Minus :size="14" />
        <span>{{ t('tagsView.closeOther') }}</span>
      </li>
      <li @click="closeLeftTags">
        <ArrowLeft :size="14" />
        <span>{{ t('tagsView.closeLeft') }}</span>
      </li>
      <li @click="closeRightTags">
        <ArrowRight :size="14" />
        <span>{{ t('tagsView.closeRight') }}</span>
      </li>
      <li @click="closeAllTags">
        <X :size="14" />
        <span>{{ t('tagsView.closeAll') }}</span>
      </li>
    </ul>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useSettingsStore } from '@/stores/settings'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const settingsStore = useSettingsStore()

const scrollContainer = ref(null)
const tagRefs = ref([])
const showScrollBtns = ref(false)

// Context menu state
const contextMenu = ref({
  visible: false,
  left: 0,
  top: 0
})
const selectedTag = ref(null)

// Computed
const tagsView = computed(() => settingsStore.tagsView)

function translateTitle(title) {
  const key = `menu.${title}`
  return t(key, title)
}

// Methods
function isActive(tag) {
  return tag.path === route.path
}

function isAffix(tag) {
  return tag?.affix
}

function addCurrentTag() {
  if (route.name && route.path !== '/login') {
    settingsStore.addTagView(route)
  }
}

function closeTag(tag) {
  if (!tag || tag.affix) return
  settingsStore.removeTagView(tag)
  if (isActive(tag)) {
    toLastView()
  }
}

function toLastView() {
  const latestView = tagsView.value[tagsView.value.length - 1]
  if (latestView) {
    router.push(latestView.path)
  } else {
    router.push('/dashboard')
  }
}

function closeOtherTags() {
  if (selectedTag.value) {
    settingsStore.closeOtherTags(selectedTag.value)
    if (!isActive(selectedTag.value)) {
      router.push(selectedTag.value.path)
    }
  }
  closeContextMenu()
}

function closeLeftTags() {
  if (selectedTag.value) {
    settingsStore.closeLeftTags(selectedTag.value)
    // If current active tag was closed, navigate to selected tag
    if (!tagsView.value.some(v => v.path === route.path)) {
      router.push(selectedTag.value.path)
    }
  }
  closeContextMenu()
}

function closeRightTags() {
  if (selectedTag.value) {
    settingsStore.closeRightTags(selectedTag.value)
    if (!tagsView.value.some(v => v.path === route.path)) {
      router.push(selectedTag.value.path)
    }
  }
  closeContextMenu()
}

function closeAllTags() {
  settingsStore.closeAllTags()
  router.push('/dashboard')
  closeContextMenu()
}

function refreshPage() {
  const view = selectedTag.value || { name: route.name, path: route.path }
  settingsStore.removeCachedView(view)
  nextTick(() => {
    router.replace({ path: '/redirect' + view.path })
  })
  closeContextMenu()
}

function openMenu(tag, e) {
  // Calculate position relative to the container
  const containerRect = scrollContainer.value?.parentElement?.getBoundingClientRect()
  if (!containerRect) return

  selectedTag.value = tag
  contextMenu.value = {
    visible: true,
    left: e.clientX - containerRect.left,
    top: e.clientY - containerRect.top
  }
}

function closeContextMenu() {
  contextMenu.value.visible = false
}

// Scrolling
function handleWheel(e) {
  if (scrollContainer.value) {
    scrollContainer.value.scrollLeft += e.deltaY || e.deltaX
  }
}

function scrollLeft() {
  if (scrollContainer.value) {
    scrollContainer.value.scrollLeft -= 200
  }
}

function scrollRight() {
  if (scrollContainer.value) {
    scrollContainer.value.scrollLeft += 200
  }
}

function checkScrollBtns() {
  if (scrollContainer.value) {
    showScrollBtns.value =
      scrollContainer.value.scrollWidth > scrollContainer.value.clientWidth
  }
}

function moveToActiveTag() {
  nextTick(() => {
    if (!scrollContainer.value) return
    const activeEl = scrollContainer.value.querySelector('.tags-view-item.active')
    if (activeEl) {
      const containerRect = scrollContainer.value.getBoundingClientRect()
      const activeRect = activeEl.getBoundingClientRect()

      if (activeRect.left < containerRect.left) {
        scrollContainer.value.scrollLeft -= containerRect.left - activeRect.left + 20
      } else if (activeRect.right > containerRect.right) {
        scrollContainer.value.scrollLeft += activeRect.right - containerRect.right + 20
      }
    }
    checkScrollBtns()
  })
}

// Watchers
watch(route, () => {
  addCurrentTag()
  moveToActiveTag()
})

// Close context menu on click outside
function handleClickOutside() {
  closeContextMenu()
}

onMounted(() => {
  addCurrentTag()
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style lang="scss" scoped>
.tags-view-container {
  height: 34px;
  width: 100%;
  background: var(--bg-header);
  border-bottom: 1px solid var(--border-light);
  display: flex;
  align-items: center;
  position: relative;
  padding: 0 2px;
  box-sizing: border-box;
  flex-shrink: 0;
}

.scroll-btn {
  width: 24px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-secondary);
  flex-shrink: 0;
  border-radius: var(--radius-sm);
  transition: all var(--transition-base);

  &:hover {
    color: var(--text-regular);
    background: var(--bg-page);
  }
}

.tags-view-wrapper {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 4px;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 0 4px;
  scroll-behavior: smooth;

  /* Hide scrollbar */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
}

.tags-view-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 26px;
  padding: 0 10px;
  font-size: var(--font-size-small);
  color: var(--text-secondary);
  background: var(--bg-page);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-sm);
  cursor: pointer;
  white-space: nowrap;
  transition: all var(--transition-base);
  user-select: none;
  flex-shrink: 0;

  &:hover {
    color: var(--primary);
    border-color: var(--primary-lightest);
    background: var(--primary-lightest);
  }

  &.active {
    color: #fff;
    background: var(--primary);
    border-color: var(--primary);

    .tag-close {
      color: rgba(255, 255, 255, 0.7);

      &:hover {
        color: #fff;
        background: rgba(255, 255, 255, 0.2);
      }
    }
  }
}

.tag-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  color: var(--text-placeholder);
  transition: all var(--transition-base);
  margin-left: 2px;
  margin-right: -4px;

  &:hover {
    color: var(--text-regular);
    background: rgba(0, 0, 0, 0.08);
  }
}

/* Context menu */
.context-menu {
  position: absolute;
  z-index: 3000;
  margin: 0;
  padding: 6px 0;
  background: var(--bg-header);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-base);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  list-style: none;
  min-width: 140px;

  li {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 16px;
    font-size: var(--font-size-secondary);
    color: var(--text-regular);
    cursor: pointer;
    transition: all var(--transition-base);

    &:hover {
      background: var(--primary-lightest);
      color: var(--primary);
    }
  }
}
</style>
